import './staticScreen.scss';
import SvgIcon from 'components/commons/icons/SvgIcon';
import TituloSeccion from 'components/commons/text/tituloSeccion/TituloSeccion';
import IconPromise from 'components/widgets/IconPromise/IconPromise';
import ImagePromise from 'components/widgets/imagePromise/ImagePromise';
import { useLanguage } from 'context/languageContext';
import DOMPurify from 'dompurify';
import UseSEO from 'hooks/useSEO/UseSEO';
import { CSSProperties, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLazyGetStaticPageByPublicCodeQuery } from 'redux/services/staticPagesService';
import { routesState } from 'redux/slices/routesSlice';
import { updateShowSubMenu } from 'redux/slices/subMenuSlices';
import EmtNewDetailSkeleton from 'screens/emtNewDetail/component/emtNewDetailSkeleton/EmtNewDetailSkeleton';
import ErrorScreen from 'screens/errorScreen/ErrorScreen';
import { ReactComponent as Ic_back } from 'resources/icons/Ic_arrow_left.svg';
import useAnalytics from 'hooks/useAnalytics/useAnalytics';

const STATIC_CONTENT = 'STATIC';
const ERROR_CONTENT = 'ERROR';

export default function IndependentStaticScreen({ style }: { style?: CSSProperties }) {
  const navigate = useNavigate();
  const location = useLocation();
  const PAGE_ID = location.pathname.split('/')[2];
  const routesSelector = useSelector(routesState);
  const currentLanguage = useLanguage();
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState<any>(null);
  const [contenidoCargado, setContenidoCargado] = useState<string | null>(null);

  const [GetStaticPageByCode] = useLazyGetStaticPageByPublicCodeQuery();
  const [, generateTitleAndMeta] = UseSEO();
  const NUMBER_OF_CHARACTERS = 550;
    const {logEventAnalytics} = useAnalytics();

  useEffect(() => {
    logEventAnalytics(`${location.pathname}_screen`);
  }, [location.pathname]);

  const renderAnswer = (res) => {
    const htmlContent = res;
    const sanitizedHtmlContent = DOMPurify.sanitize(htmlContent);
    return sanitizedHtmlContent;
  };

  const loadContentPageByCode = async (code) => {
    try {
      const data = await GetStaticPageByCode({ code, language: currentLanguage });
      if (data.isError || String(data.status) === 'rejected') {
        throw new Error('La página no existe');
      } else {
        setPageData(data?.data);
        setContenidoCargado(STATIC_CONTENT); // Solo se actualiza si la consulta es exitosa
        return data?.data?.id;
      }
    } catch (error) {
      setContenidoCargado(ERROR_CONTENT); // Solo se actualiza si hay un error
    } finally {
      setLoading(true); // Siempre se desactiva el loading al final
    }
  };

  /* const loadContentMenuCard = async (paginaId) => {
    try {
      const res = await GetStaticPage({ pageId: paginaId, language: currentLanguage });
      if (res.isSuccess) {
        return res?.data;
      }
    } catch (error) {
      console.error('Error al cargar el menú:', error);
    }
  }; */

  const isValidPageId = (id: string) => /^[a-zA-Z0-9_-]+$/.test(id);

  const init = async () => {
    try {
      setLoading(true); // Activar loading al inicio
      setContenidoCargado(null); // Reiniciar el estado de contenidoCargado
      setPageData(null);

      if (PAGE_ID && isNaN(Number(PAGE_ID)) && isValidPageId(PAGE_ID)) {
        await loadContentPageByCode(PAGE_ID).catch((e) => {});
        
      } else {
        // Manejo de rutas sin PAGE_ID
        throw new Error('Error al cargar página estática');
      }
    } catch (error) {
      setContenidoCargado(ERROR_CONTENT); // Solo se actualiza si hay un error
    } finally {
      setTimeout(()=> setLoading(false), 150); // Siempre se desactiva el loading al final
    }
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updateShowSubMenu(true));
    init()
      .then(() => {
        window.scroll({
          top: 0,
          left: 100,
          behavior: 'smooth',
        });
      })
      .catch((e) => {
        console.error('Error al ejecutar init:', e);
      });
  }, [location, currentLanguage, routesSelector]);

  const renderStaticContent = () => {
    return (
      <>
        {generateTitleAndMeta(pageData?.title, pageData?.seoInfo ?? pageData?.summary)}
        {pageData && (
          <div className='static-screen-main'>
            {PAGE_ID && location.pathname.includes('emt-palma') && (
              <div className='static-go-back'>
                <SvgIcon Icon={Ic_back} onClick={() => navigate(-1)} />
                <hr />
                <span>Volver</span>
              </div>
            )}
            <div className='static-screen-header'>
              <IconPromise idIcon={Number(pageData?.iconId)} />
              <h1 style={{ marginTop: 0 }}>{pageData?.title}</h1>
            </div>
            {/* BODY */}
            <div className='container'>
              {pageData?.imageId ? (
                <div
                  className={pageData?.body?.length > NUMBER_OF_CHARACTERS ? 'col-12' : 'col-6'}
                  style={{ order: pageData?.body?.length > NUMBER_OF_CHARACTERS ? '0' : '2' }}
                >
                  <div className='static-img-wrapper'>
                    <ImagePromise imageId={pageData?.imageId} className='static-screen-img' />
                  </div>
                </div>
              ) : null}
              <div
                className={pageData?.body?.length > NUMBER_OF_CHARACTERS || (!pageData?.imageId) ? 'col-12' : 'col-6'}
                style={{ height: '100%' }}
              >
                <TituloSeccion text={pageData?.summary} />
                <div dangerouslySetInnerHTML={{ __html: renderAnswer(pageData?.body) }}></div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div style={{flex:1, flexDirection:'column'}}>
      <section className="static-wapper">
        <div className="container">
          <main className="static-main col-12" style={{ ...style }}>
            {loading ? (
              <div className="static-loader-container">
                <EmtNewDetailSkeleton />
              </div>
            ) : contenidoCargado === STATIC_CONTENT ? (
              renderStaticContent()
            ) : contenidoCargado === ERROR_CONTENT ? (
              <ErrorScreen errorCode={404} />
            ) : null}
          </main>
        </div>
      </section>
    </div>
  );
}